import {Component, ViewChild, ElementRef, AfterViewInit, OnInit} from '@angular/core';
import {User} from '@tc-core/model/it/codegen/tbx/api/authority/v2/users/user';
import {DataKey, DataStore} from '@tc-core/util/framework/data-store.service';

import {buildHotelSearchUrl, mapToHotelSummary} from '../utils/hotel.util';
import {scrollToBottom} from '../utils/dom.util';

import {BotMessage, Message} from '../models/message.model';
import {ChatComponent, ComponentPayload, PromptSuggestionList, Suggestion} from '../models/chat-component.model';
import {ChatAgentService} from '../services/chat-agent.service';
import {CHAT_CONSTANTS} from '../utils/app.constants';
import {TC} from '@tc-core/util';
import {ConfigLoader} from '@tc-core/util/framework';


@Component({
  selector: 'app-chat-panel',
  templateUrl: './chat-panel.component.html',
  styleUrls: ['./chat-panel.component.scss']
})
export class ChatPanelComponent implements AfterViewInit, OnInit {
  isOpen = false;
  showBubbleInfo = true;
  inputMsg = '';
  user: User = null;
  messages: Message[] = [];
  suggestions: string[] = [];
  moduleConfigs: any;
  sessionId: string;
  isProcessing = false;
  useMockService = false;


  @ViewChild('chatContent') chatContent!: ElementRef<HTMLDivElement>;


  constructor(protected dataStore: DataStore, private chatAgentService: ChatAgentService, protected configLoader: ConfigLoader) {
  }

  ngOnInit(): void {
    this.loadConfigs();
    this.user = this.dataStore.get(DataKey.userDetail).getValue();
    this.sessionId = sessionStorage.getItem(CHAT_CONSTANTS.STORAGE_KEYS.SESSION_ID);
  }

  ngAfterViewInit(): void {

    // Hide the chat bubble info after 3 seconds
    setTimeout(() => {
      this.showBubbleInfo = false;
    }, CHAT_CONSTANTS.TIMERS.HIDE_BUBBLE_INFO);
  }

  toggleChat() {
    this.isOpen = !this.isOpen;
  }


  sendSuggestion(suggestion: string) {
    if (this.isProcessing) { // Prevent sending if processing
      return;
    }
    this.handleMessage(suggestion);
  }

  sendMessage() {
    if (!this.inputMsg.trim() || this.isProcessing) {
      return;
    }
    this.handleMessage(this.inputMsg);
    this.inputMsg = '';
  }

  private async handleMessage(query: string) {
    this.isProcessing = true; // Set processing flag
    // Add user message
    this.messages.push({sender: 'user', timestamp: new Date(), text: query});
    scrollToBottom(this.chatContent);  // Scroll to bottom after adding user message

    // Create initial bot message with empty notifications array
    const botMsg: BotMessage = {sender: 'bot', timestamp: new Date(), notifications: [], content: [], showNotifications: true};
    this.messages.push(botMsg);
    scrollToBottom(this.chatContent);  // Scroll to bottom after adding initial bot message

    try {
      // Check if clientId exists
      if (!this.user?.clientId) {
        console.error('Client ID not available');
        botMsg.content!.push({type: 'text', text: 'Error: Client ID not available.'});
        botMsg.showNotifications = false;
        scrollToBottom(this.chatContent);
        return;
      }

      let response: any;
      try {
        response = await this.chatAgentService.sendMessage(query, this.sessionId, this.user, this.useMockService);
      } catch (err) {
        console.error('Error sending message via agent service:', err);
        botMsg.content!.push({type: 'text', text: err.message});
        botMsg.showNotifications = false;
        scrollToBottom(this.chatContent);
        this.isProcessing = false;
        return;
      }

      if (!response.body) {
        throw new Error('No stream body');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let bufferedContent = ''; // Buffer for non-notification content

      while (true) {
        const {value, done} = await reader.read();
        if (done) {
          // Stream is complete, now process the buffered content
          this.processBufferedContent(botMsg, bufferedContent);
          scrollToBottom(this.chatContent); // Scroll to bottom after processing buffered content
          this.isProcessing = false; // Reset processing flag
          break;
        }

        const chunk = decoder.decode(value, {stream: true});
        let remaining = chunk;
        let lastIndex = 0;

        // Regex to match notifications only in real-time
        const notificationRegex = CHAT_CONSTANTS.REGEX.NOTIFICATION;
        let match;

        while ((match = notificationRegex.exec(remaining)) !== null) {
          // Push any text before this notification to the buffer
          const textBefore = remaining.slice(lastIndex, match.index).trim();
          if (textBefore) {
            bufferedContent += textBefore;
          }

          // Process notification in real-time
          if (match[1]) {
            botMsg.notifications!.push(match[1].trim());
            scrollToBottom(this.chatContent);  // Scroll to bottom after adding notifications
          }

          lastIndex = notificationRegex.lastIndex;
        }

        // Buffer any remaining text after the last notification in this chunk
        const remainingText = remaining.slice(lastIndex).trim();
        if (remainingText) {
          bufferedContent += remainingText;
        }
      }

      console.log('Complete Bot Message:', botMsg);
    } catch (err) {
      console.error('Error streaming bot message:', err);
      botMsg.content!.push({type: 'text', text: 'Failed to get a response from the agent.'});
      botMsg.showNotifications = false;
      scrollToBottom(this.chatContent); // Scroll to bottom even on error
      this.isProcessing = false; // Reset processing flag
    }
  }

  private processBufferedContent(botMsg: BotMessage, bufferedContent: string) {
    let remaining = bufferedContent;
    let lastIndex = 0;

    // Regex to match tables and components
    const contentRegex = CHAT_CONSTANTS.REGEX.CONTENT;
    let match;


    // Find the earliest marker to truncate content
    let earliestMarkerIndex = -1;
    for (const marker of CHAT_CONSTANTS.EXCLUDE_MARKERS) {
      const markerIndex = remaining.indexOf(marker);
      if (markerIndex !== -1 && (earliestMarkerIndex === -1 || markerIndex < earliestMarkerIndex)) {
        earliestMarkerIndex = markerIndex;
      }
    }

    // Truncate content at the earliest marker (if any)
    if (earliestMarkerIndex !== -1) {
      remaining = remaining.substring(0, earliestMarkerIndex).trim();
    }

    while ((match = contentRegex.exec(remaining)) !== null) {
      // Push any text before this token (if not empty)
      const textBefore = remaining.slice(lastIndex, match.index).trim();
      if (textBefore) {
        botMsg.content!.push({type: 'text', text: textBefore});
      }

      // Determine what type of token matched
      if (match[1]) {
        // Table: Process as raw string initially
        const tableContent = match[1].trim();
        let contentType: string = 'dynamic'; // Default to dynamic
        let tableData: any = {content: []}; // Default to empty parsed content

        try {
          // Parse the table content as JSON
          const parsedContent = JSON.parse(tableContent);
          const rows = parsedContent.content || [];
          const parsedRows: any[] = [];

          // Ensure rows are processed as objects
          for (const row of rows) {
            if (typeof row === 'object' && row !== null) {
              parsedRows.push(row);
            }
          }

          if (parsedRows.length > 0 && parsedRows.every(r => typeof r === 'object' && Object.keys(r).length > 0)) {
            // Determine table type based on first column key
            const firstKeys = parsedRows.map(row => Object.keys(row)[0]?.toLowerCase());
            if (firstKeys.length > 0 && firstKeys.every(key => key && key.includes('hotel'))) {
              contentType = 'hotel';
              // Map to HotelSummary using the new method
              const hotelData = mapToHotelSummary(parsedRows);
              tableData = {content: hotelData};
            } else {
              contentType = 'dynamic';
              tableData = {content: parsedRows}; // Store parsed key-value pairs
            }
          } else {
            contentType = 'dynamic';
            tableData = {content: parsedRows}; // Store parsed rows even if empty
          }

          botMsg.content!.push({type: 'table', table: tableData, contentType: contentType});
        } catch (e) {
          console.warn('Failed to parse table structure, storing as empty content:', e);
          botMsg.content!.push({type: 'table', table: {content: []}, contentType: 'dynamic'});
        }
      } else if (match[2] || match[3]) {
        // CHAT_VIEW + COMPONENT or Standalone COMPONENT
        const componentContent = (match[2] || match[3]).trim();
        try {
          const componentObj = JSON.parse(componentContent) as ChatComponent;
          if (componentObj.component_type === 'PromptSuggestionList') {
            botMsg.content!.push({type: 'component', component: componentObj as PromptSuggestionList});
          } else {
            botMsg.content!.push({type: 'component', component: componentObj});
          }
        } catch (e) {
          console.warn('Failed to parse component as JSON, storing as raw string:', e);
          botMsg.content!.push({type: 'component', component: componentContent});
        }
      }

      lastIndex = contentRegex.lastIndex;
    }

    // Push any remaining text after the last token (if not empty)
    const remainingText = remaining.slice(lastIndex).trim();
    if (remainingText) {
      botMsg.content!.push({type: 'text', text: remainingText});
    }

    // Hide notification content if content is present
    if (botMsg.content!.length > 0) {
      botMsg.showNotifications = false;
    }
    scrollToBottom(this.chatContent); // Scroll to bottom after processing content
  }


  // Handle suggestion clicks
  handleSuggestionClick(suggestion: Suggestion) {
    if (suggestion.payload && suggestion.payload.opCode) {
      const opCode = suggestion.payload.opCode;
      const attributes = suggestion.payload.attributes;

      if (opCode === CHAT_CONSTANTS.OPCODES.HOTEL_SEARCH) {
        this.handleHotelSearch(suggestion.payload);
        return;
      } else if (opCode === CHAT_CONSTANTS.OPCODES.AGENT_PERFORM) {
        if (attributes && attributes['query']) {
          this.handleMessage(attributes['query']);
        }
        return;
      } else if (opCode === CHAT_CONSTANTS.OPCODES.BOOKING_VIEW && Array.isArray(attributes)) {
        // Handle booking view as the else condition
        const bkgAttr = attributes.find(attr => attr.name === 'bkgId');
        if (bkgAttr?.value) {
          const bookingId = bkgAttr.value;
          const baseUrl = this.getBaseUrl();
          const url = `${baseUrl}/b2b-booking/?bkgId=${bookingId}`;
          window.open(url, '_blank');

          // Optional confirmation message in chat
          this.messages.push({
            sender: 'bot',
            timestamp: new Date(),
            content: [{type: 'text', text: `Opening booking details for ID ${bookingId}...`}],
            showNotifications: false
          });
          scrollToBottom(this.chatContent);
        }
        return;
      } else {
        console.log('Processing unknown payload:', suggestion.payload);
      }
    }

    // Fallback: send suggestion as user message
    this.sendSuggestion(suggestion.message);
  }

  // New method to handle HTL_SEARCH like the JavaScript version
  private handleHotelSearch(payload: ComponentPayload) {
    // Validate payload
    if (!payload.attributes || !Array.isArray(payload.attributes)) {
      console.error('Invalid payload for hotel search');
      this.messages.push({
        sender: 'bot',
        timestamp: new Date(),
        content: [{type: 'text', text: 'Error: Invalid hotel search parameters.'}],
        showNotifications: false
      });
      scrollToBottom(this.chatContent);
      return;
    }

// Get hotel name from payload attributes
    const hotelAttr = payload.attributes?.find(attr => attr.name === 'hotel');
    const hotelName = hotelAttr?.value || 'hotels in Dubai';

// Build URL
    const url = buildHotelSearchUrl(this.getBaseUrl(), payload.attributes, this.user);
    window.open(url, '_blank');

// Add confirmation message
    this.messages.push({
      sender: 'bot',
      timestamp: new Date(),
      content: [{type: 'text', text: `Opening hotel search results for ${hotelName}...`}],
      showNotifications: false
    });
    scrollToBottom(this.chatContent);
  }


  getBaseUrl(): string {
    const urlObj = new URL(window.location.href);
    return `${urlObj.protocol}//${urlObj.host}`;
  }

  loadConfigs() {
    const rawSuggestions: string | null = this.configLoader.getModuleConfig(
      'SURF_CHAT_BOT_SUGGESTIONS',
      TC.MODULE_NAME.SURF_B2B
    );
    if (rawSuggestions && rawSuggestions.trim() !== '') {
      this.suggestions = rawSuggestions
        .split('|')
        .map(s => s.trim())
        .filter(s => s.length > 0);
    } else {
      this.suggestions = [];
    }
    const agentAPI: string | null = this.configLoader.getModuleConfig(
      'SURF_CHAT_BOT_AGENT_API',
      TC.MODULE_NAME.SURF_B2B
    );
    if (agentAPI) {
      CHAT_CONSTANTS.URLS.AGENT = agentAPI;
    }
  }
}
